<template>
   <v-dialog v-model="dlgnew" max-width="920" persistent scrollable transition="dialog-bottom-transition">
      <v-card class="new-member-dialog">
         <div class="new-member-header">
            <div>
               <div class="new-member-eyebrow">Admin Membership</div>
               <div class="new-member-title">New Membership</div>
               <div class="new-member-subtitle">
                  Register a new member profile and set up the account for membership access.
               </div>
            </div>
            <v-btn icon dark @click="cancelAdd" :disabled="saving">
               <v-icon>mdi-close</v-icon>
            </v-btn>
         </div>

         <v-card-text class="new-member-body">
            <v-alert v-if="message" :type="alerttype" dense outlined class="mb-4">
               {{ message }}
            </v-alert>

            <v-form v-model="isvalid" ref="newMemberForm">
               <div class="form-section">
                  <div class="section-heading">
                     <v-icon small color="primary">mdi-account-badge</v-icon>
                     <span>Identity</span>
                  </div>
                  <v-row dense>
                     <v-col cols="12" md="4">
                        <v-text-field
                           v-model="formmodel.salutation"
                           label="Salutation"
                           outlined
                           dense
                           hide-details="auto"
                        />
                     </v-col>
                     <v-col cols="12" md="8">
                        <v-text-field
                           v-model="formmodel.fullname"
                           label="Full Name *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.fullname"
                           @input="clearFieldError('fullname')"
                        />
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="formmodel.icpassport"
                           label="IC / Passport No. *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.icpassport"
                           @input="clearFieldError('icpassport')"
                        />
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="formmodel.memberid"
                           label="Member ID"
                           outlined
                           dense
                           hide-details="auto"
                           hint="Optional. Admin can assign this now or later."
                           persistent-hint
                        />
                     </v-col>
                  </v-row>
               </div>

               <div class="form-section">
                  <div class="section-heading">
                     <v-icon small color="primary">mdi-card-account-mail</v-icon>
                     <span>Contact & Zone</span>
                  </div>
                  <v-row dense>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="formmodel.contactemail"
                           label="Email *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.contactemail"
                           @input="clearFieldError('contactemail')"
                        />
                     </v-col>
                     <v-col cols="12" md="6">
                        <v-text-field
                           v-model="formmodel.contactmobile"
                           label="Mobile No. *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.contactmobile"
                           @input="clearFieldError('contactmobile')"
                        />
                     </v-col>
                     <v-col cols="12">
                        <v-select
                           v-model="formmodel.homestate"
                           :items="stateOptions"
                           item-text="title"
                           item-value="const"
                           label="Home State / Region *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.homestate"
                           @change="clearFieldError('homestate')"
                        />
                        <div class="field-note">Used for zone-based election and nomination access.</div>
                     </v-col>
                  </v-row>
               </div>

               <div class="form-section">
                  <div class="section-heading">
                     <v-icon small color="primary">mdi-shield-account</v-icon>
                     <span>Membership & Login</span>
                  </div>
                  <v-row dense>
                     <v-col cols="12" md="4">
                        <v-select
                           v-model="formmodel.membertype"
                           :items="memberTypeOptions"
                           item-text="title"
                           item-value="const"
                           label="Membership Type *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.membertype"
                           @change="clearFieldError('membertype')"
                        />
                     </v-col>
                     <v-col cols="12" md="4">
                        <v-select
                           v-model="formmodel.memberstatus"
                           :items="memberStatusOptions"
                           item-text="title"
                           item-value="const"
                           label="Membership Status *"
                           outlined
                           dense
                           hide-details="auto"
                           :error-messages="fieldErrors.memberstatus"
                           @change="clearFieldError('memberstatus')"
                        />
                     </v-col>
                     <v-col cols="12" md="4">
                        <v-text-field
                           v-model="formmodel.password"
                           label="Temporary Password *"
                           outlined
                           dense
                           hide-details="auto"
                           :type="showPassword ? 'text' : 'password'"
                           :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                           :error-messages="fieldErrors.password"
                           @click:append="showPassword = !showPassword"
                           @input="clearFieldError('password')"
                        />
                     </v-col>
                  </v-row>
               </div>

               <v-alert dense text type="info" class="mt-2 mb-0">
                  Email, IC / Passport No. and Mobile No. must be unique to avoid duplicate member accounts.
               </v-alert>
            </v-form>
         </v-card-text>

         <v-card-actions class="new-member-actions">
            <v-btn text @click="cancelAdd" :disabled="saving">
               Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" class="px-6" @click="addItem" :loading="saving" :disabled="saving">
               <v-icon left>mdi-content-save</v-icon>
               Add Member
            </v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>
</template>

<script>
const axios = require('axios')
import VJsf from '@koumoul/vjsf/lib/VJsf.js'
import '@koumoul/vjsf/lib/VJsf.css'
import '@koumoul/vjsf/lib/deps/third-party.js'
import GeneralOptions from '../Forms/GeneralOptions.js'
import NewMemberModel from '../Forms/NewMemberModel.js'
import NewMemberSchema from '../Forms/NewMemberSchema.js'

export default {
   name: 'DialogMemberNew',
   components: { VJsf },
   props: {},
   data: () => ({
      formoptions: GeneralOptions,
      formschema: NewMemberSchema,
      formmodel: NewMemberModel,
      isvalid: false,
      dlgnew: false,
      saving: false,
      message: '',
      alerttype: 'error',
      fieldErrors: {},
      showPassword: false,
   }),

   watch: {

   },

   computed: {
      stateOptions() {
         return this.formschema.properties.homestate.oneOf
      },

      memberTypeOptions() {
         return this.formschema.properties.membertype.oneOf
      },

      memberStatusOptions() {
         return this.formschema.properties.memberstatus.oneOf
      },
   },

   methods: {
      show() {
         this.formmodel = Object.assign({}, NewMemberModel)
         this.message = ''
         this.alerttype = 'error'
         this.fieldErrors = {}
         this.showPassword = false
         this.dlgnew = true
      },

      cancelAdd() {
         this.dlgnew = false
      },

      addItem() {
         const validationMessage = this.validateForm()
         if (validationMessage) {
            this.alerttype = 'error'
            this.message = validationMessage
            return
         }

         this.saving = true
         this.message = ''

         axios.post('/admin/member/new', this.formmodel)
            .then((response) => {
               const result = this.parseResponse(response.data)
               if (result.status === 'OK') {
                  this.alerttype = 'success'
                  this.message = result.message || 'New member added successfully.'
                  this.$emit('MemberAdded')
                  setTimeout(() => {
                     this.dlgnew = false
                  }, 600)
               } else {
                  this.alerttype = 'error'
                  this.message = result.error || result.message || 'Unable to add new member.'
               }
            })
            .catch((error) => {
               this.alerttype = 'error'
               this.message = this.formatError(error)
            })
            .finally(() => {
               this.saving = false
            })
      },

      validateForm() {
         this.fieldErrors = {}
         const requiredFields = [
            { key: 'fullname', label: 'Full name' },
            { key: 'icpassport', label: 'IC / Passport No.' },
            { key: 'contactemail', label: 'Email' },
            { key: 'contactmobile', label: 'Mobile No.' },
            { key: 'homestate', label: 'Home State / Region' },
            { key: 'membertype', label: 'Membership type' },
            { key: 'memberstatus', label: 'Membership status' },
            { key: 'password', label: 'Password' },
         ]

         for (const field of requiredFields) {
            const value = this.formmodel[field.key]
            if (value === null || value === undefined || String(value).trim() === '') {
               this.$set(this.fieldErrors, field.key, [`${field.label} is required.`])
               return `${field.label} is required.`
            }
         }

         if (!/.+@.+\..+/.test(this.formmodel.contactemail)) {
            this.$set(this.fieldErrors, 'contactemail', ['Email must be a valid email address.'])
            return 'Email must be a valid email address.'
         }

         if (String(this.formmodel.password).length < 8) {
            this.$set(this.fieldErrors, 'password', ['Password must be at least 8 characters.'])
            return 'Password must be at least 8 characters.'
         }

         return ''
      },

      clearFieldError(field) {
         if (this.fieldErrors[field]) {
            this.$delete(this.fieldErrors, field)
         }
      },

      parseResponse(data) {
         if (typeof data === 'string') {
            try {
               return JSON.parse(data)
            } catch (error) {
               return { status: 'ERR', error: data }
            }
         }
         return data || {}
      },

      formatError(error) {
         if (error.response && error.response.data) {
            const data = error.response.data
            if (data.errors) {
               return Object.values(data.errors).flat().join(' ')
            }
            if (data.message) {
               return data.message
            }
         }
         return 'Unable to add new member. Please check the details and try again.'
      }

   },
}
</script>

<style scoped>
.new-member-dialog {
   border-radius: 10px;
   overflow: hidden;
}

.new-member-header {
   align-items: flex-start;
   background: linear-gradient(120deg, #0b8f86 0%, #1768b7 58%, #2554d8 100%);
   color: #fff;
   display: flex;
   justify-content: space-between;
   padding: 24px 28px;
}

.new-member-eyebrow {
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 1.8px;
   opacity: .85;
   text-transform: uppercase;
}

.new-member-title {
   font-size: 26px;
   font-weight: 700;
   line-height: 1.2;
   margin-top: 4px;
}

.new-member-subtitle {
   font-size: 14px;
   line-height: 1.45;
   margin-top: 6px;
   max-width: 620px;
   opacity: .92;
}

.new-member-body {
   background: #f4f7fb;
   padding: 22px 24px !important;
}

.form-section {
   background: #fff;
   border: 1px solid #dbe4f0;
   border-radius: 8px;
   margin-bottom: 14px;
   padding: 18px;
}

.section-heading {
   align-items: center;
   color: #17233c;
   display: flex;
   font-size: 13px;
   font-weight: 700;
   gap: 8px;
   letter-spacing: .8px;
   margin-bottom: 14px;
   text-transform: uppercase;
}

.field-note {
   color: #63748a;
   font-size: 12px;
   margin-top: 5px;
}

.new-member-actions {
   background: #fff;
   border-top: 1px solid #dbe4f0;
   padding: 14px 24px;
}
</style>
